import { useState } from 'react'

type Choice = 'all' | 'long' | 'manual'

interface Props {
  submitting: boolean
  error: string | null
  onSubmit: (whatToPublish: Choice, backfillRequested: boolean) => void
}

/**
 * Шаг 3 активации — Что публиковать.
 *
 * 3 radio по §4 спеки:
 *   - Все посты (по умолчанию)
 *   - Только длинные, >500 символов
 *   - Выбрать вручную
 *
 * + опциональный тумблер «бэкфилл архива» (по умолчанию false — публикуем только
 *   500 последних + новые после подключения, как в R2 спеки v3.2).
 */
export function StepChoosing({ submitting, error, onSubmit }: Props) {
  const [choice, setChoice] = useState<Choice>('all')
  const [backfill, setBackfill] = useState(false)

  function handleSubmit() {
    if (submitting) return
    onSubmit(choice, backfill)
  }

  return (
    <div className="step-choosing">
      <div className="ch-header">
        <h2 className="ch-title">Что публиковать?</h2>
        <p className="ch-sub">
          Выберите какие посты канала пойдут в&nbsp;поиск. Можно изменить в&nbsp;любой момент в&nbsp;настройках.
        </p>
      </div>

      <div className="ch-options" role="radiogroup">
        <label className={`ch-opt ${choice === 'all' ? 'on' : ''}`}>
          <input type="radio" name="ch" checked={choice === 'all'} onChange={() => setChoice('all')} />
          <span className="ch-radio" />
          <div className="ch-opt-body">
            <div className="ch-opt-title">Все посты</div>
            <div className="ch-opt-sub">
              Публикуем каждый пост канала автоматически. Самый широкий охват — поисковики получают максимум контента.
            </div>
          </div>
          <div className="ch-opt-pill recommend">Рекомендуем</div>
        </label>

        <label className={`ch-opt ${choice === 'long' ? 'on' : ''}`}>
          <input type="radio" name="ch" checked={choice === 'long'} onChange={() => setChoice('long')} />
          <span className="ch-radio" />
          <div className="ch-opt-body">
            <div className="ch-opt-title">Только длинные посты — больше 500&nbsp;символов</div>
            <div className="ch-opt-sub">
              Короткие посты («доброе утро», реакции, односложные) пропускаем — они плохо ранжируются в&nbsp;поиске.
            </div>
          </div>
        </label>

        <label className={`ch-opt ${choice === 'manual' ? 'on' : ''}`}>
          <input type="radio" name="ch" checked={choice === 'manual'} onChange={() => setChoice('manual')} />
          <span className="ch-radio" />
          <div className="ch-opt-body">
            <div className="ch-opt-title">Выбрать вручную</div>
            <div className="ch-opt-sub">
              Новые посты <b>не</b> попадают в&nbsp;поиск автоматически. Вы&nbsp;сами выбираете нужные на&nbsp;странице «Посты».
            </div>
          </div>
        </label>
      </div>

      <label className={`ch-backfill ${backfill ? 'on' : ''}`}>
        <input type="checkbox" checked={backfill} onChange={(e) => setBackfill(e.target.checked)} />
        <span className="bf-box">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6L9 17l-5-5" />
          </svg>
        </span>
        <div className="bf-body">
          <div className="bf-title">Опубликовать все архивные посты</div>
          <div className="bf-sub">
            По умолчанию мы&nbsp;берём только последние 500&nbsp;постов. Если хотите весь архив — отметьте. Это
            займёт 2-3&nbsp;дня, мы&nbsp;обработаем постепенно.
          </div>
        </div>
      </label>

      {error && <div className="ch-error">{error}</div>}

      <div className="ch-actions">
        <button className="cta-primary" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Запускаем…' : 'Готово — запустить'}
          {!submitting && (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          )}
        </button>
      </div>

      <style>{`
        .step-choosing {
          display: flex;
          flex-direction: column;
          gap: 22px;
          max-width: 720px;
          margin: 0 auto;
          width: 100%;
        }
        .ch-header { text-align: center; }
        .ch-title {
          font-size: 24px;
          font-weight: 800;
          margin: 0 0 8px;
          color: var(--tg-ink, #111);
        }
        .ch-sub {
          font-size: 14px;
          color: var(--tg-mute, #5b6678);
          line-height: 1.55;
          margin: 0;
        }
        .ch-options {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .ch-opt {
          display: flex;
          align-items: flex-start;
          gap: 14px;
          padding: 16px 18px;
          background: var(--tg-surface, #fff);
          border: 1.5px solid var(--tg-line-2, #f0f0f4);
          border-radius: 12px;
          cursor: pointer;
          transition: border-color 150ms, background 150ms;
        }
        .ch-opt:hover { border-color: var(--tg-line-3, #d0d0d8); }
        .ch-opt.on {
          border-color: var(--tg-blue, #0066ff);
          background: var(--tg-blue-bg, #ebf2ff);
        }
        .ch-opt input[type="radio"] {
          position: absolute;
          opacity: 0;
          pointer-events: none;
        }
        .ch-radio {
          width: 20px;
          height: 20px;
          flex-shrink: 0;
          margin-top: 2px;
          border-radius: 50%;
          border: 1.5px solid var(--tg-line-3, #d0d0d8);
          background: white;
          position: relative;
          transition: border-color 150ms;
        }
        .ch-opt.on .ch-radio { border-color: var(--tg-blue, #0066ff); }
        .ch-opt.on .ch-radio::after {
          content: '';
          position: absolute;
          inset: 4px;
          border-radius: 50%;
          background: var(--tg-blue, #0066ff);
        }
        .ch-opt-body { flex: 1; min-width: 0; }
        .ch-opt-title {
          font-size: 14.5px;
          font-weight: 700;
          color: var(--tg-ink, #111);
          margin-bottom: 4px;
        }
        .ch-opt-sub {
          font-size: 13px;
          color: var(--tg-mute, #5b6678);
          line-height: 1.5;
        }
        .ch-opt-pill {
          padding: 3px 8px;
          font-size: 10.5px;
          font-weight: 800;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          border-radius: 999px;
          flex-shrink: 0;
        }
        .ch-opt-pill.recommend {
          background: rgba(0, 168, 96, 0.15);
          color: var(--tg-up-press, #008a52);
        }
        .ch-backfill {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 14px 16px;
          background: var(--tg-paper, #fafafd);
          border: 1px dashed var(--tg-line-3, #d0d0d8);
          border-radius: 12px;
          cursor: pointer;
        }
        .ch-backfill input[type="checkbox"] {
          position: absolute;
          opacity: 0;
          pointer-events: none;
        }
        .bf-box {
          width: 20px;
          height: 20px;
          flex-shrink: 0;
          margin-top: 2px;
          border-radius: 6px;
          border: 1.5px solid var(--tg-line-3, #d0d0d8);
          background: white;
          display: flex;
          align-items: center;
          justify-content: center;
          color: transparent;
        }
        .bf-box svg { width: 12px; height: 12px; }
        .ch-backfill.on .bf-box {
          background: var(--tg-blue, #0066ff);
          border-color: var(--tg-blue, #0066ff);
          color: white;
        }
        .bf-title {
          font-size: 13.5px;
          font-weight: 700;
          color: var(--tg-ink, #111);
          margin-bottom: 2px;
        }
        .bf-sub {
          font-size: 12.5px;
          color: var(--tg-mute, #5b6678);
          line-height: 1.5;
        }
        .ch-error {
          padding: 10px 14px;
          background: rgba(255, 107, 107, 0.1);
          border: 1px solid rgba(255, 107, 107, 0.3);
          color: #c53030;
          border-radius: 10px;
          font-size: 13px;
        }
        .ch-actions {
          display: flex;
          justify-content: center;
        }
        .cta-primary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 13px 28px;
          background: var(--tg-blue, #0066ff);
          color: white;
          font-weight: 700;
          font-size: 14px;
          border: 0;
          border-radius: 12px;
          cursor: pointer;
          font-family: inherit;
          transition: background 150ms;
          box-shadow: 0 4px 16px -4px rgba(0,102,255,0.35);
        }
        .cta-primary:hover:not(:disabled) { background: var(--tg-blue-press, #0052cc); }
        .cta-primary:disabled { opacity: 0.6; cursor: not-allowed; }
        .cta-primary svg { width: 16px; height: 16px; }
      `}</style>
    </div>
  )
}
